import type {
  ArchivedRun,
  Json,
  Playbook,
  WorkspacePlaybook,
  WorkspaceWithPlaybooks,
  WorkspaceWithPlaybooksDetailed,
  PlaybookBrief,
  PlaybookBriefEnriched,
  WorkspacePlaybookStatus,
  BlueprintStep,
  PlaybookFeedback,
  PlaybookOutput,
  PlaybookRevision,
  CreatePlaybookOutputRequest,
  UpdatePlaybookOutputRequest,
} from '@doneverse/shared'
import { api, isConflictError } from '../api/index.js'

function withUserId(path: string, userId?: string | null) {
  if (!userId) return path
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}user_id=${encodeURIComponent(userId)}`
}

export const playbookService = {
  fetchWorkspaceDetailed: async (founderId?: string): Promise<WorkspaceWithPlaybooksDetailed> => {
    const path = founderId
      ? `/workspace?detailed=true&founder_id=${encodeURIComponent(founderId)}`
      : '/workspace?detailed=true'
    return api.get(path).json<WorkspaceWithPlaybooksDetailed>()
  },

  fetchBlueprintSteps: async (): Promise<BlueprintStep[]> => {
    return api.get('/v1/blueprint/steps').json<BlueprintStep[]>()
  },

  fetchUserPlaybookIds: async (): Promise<string[]> => {
    const workspace = await api.get('/workspace').json<WorkspaceWithPlaybooks>()
    return workspace.playbooks.map((wp) => wp.playbook_id)
  },

  fetchAllMasterPlaybooks: async (): Promise<Playbook[]> => {
    return api.get('/playbooks').json<Playbook[]>()
  },

  fetchPlaybook: async (slug: string): Promise<Playbook> => {
    return api.get(`/playbooks/${slug}`).json<Playbook>()
  },

  fetchAssignedPlaybooks: async (): Promise<PlaybookBriefEnriched[]> => {
    return api.get('/do/playbooks').json<PlaybookBriefEnriched[]>()
  },

  fetchUnreadAssignments: async (
    doerId: string,
    limit: number = 5,
  ): Promise<{ data: PlaybookBriefEnriched[]; count: number }> => {
    void doerId
    // We fetch active playbooks as a proxy for 'unread' or 'active' assignments
    const data = await api.get('/do/playbooks?status=in_progress').json<PlaybookBriefEnriched[]>()

    // Return limited data and total count
    return {
      data: data.slice(0, limit),
      count: data.length,
    }
  },

  createBrief: async (
    playbookId: string,
    briefContent: Json,
    options?: { draft?: boolean; founderId?: string | null },
  ): Promise<PlaybookBrief> => {
    return api
      .post(
        {
          playbook_id: playbookId,
          brief: briefContent,
          ...(options?.draft ? { draft: true } : {}),
        },
        withUserId('/do/playbooks', options?.founderId),
      )
      .json<PlaybookBrief>()
  },

  saveBriefDraft: async (input: {
    playbookId: string
    playbookBriefId: string | null | undefined
    briefContent: Json
    founderId?: string | null
  }): Promise<PlaybookBrief> => {
    if (!input.playbookBriefId) {
      return playbookService.createBrief(input.playbookId, input.briefContent, {
        draft: true,
        founderId: input.founderId,
      })
    }

    return api
      .put(
        {
          brief: input.briefContent,
          status: 'draft',
        },
        withUserId(`/do/playbooks/${input.playbookBriefId}`, input.founderId),
      )
      .json<PlaybookBrief>()
  },

  setWorkspacePlaybookStatus: async (
    workspacePlaybookId: string,
    status: WorkspacePlaybookStatus,
    options?: { tags?: string[]; tag_notes?: string | null },
  ): Promise<WorkspacePlaybook> => {
    return api
      .patch(
        {
          status,
          ...(options?.tags !== undefined && { tags: options.tags }),
          ...(options?.tag_notes !== undefined && { tag_notes: options.tag_notes }),
        },
        `/workspace/playbooks/${workspacePlaybookId}/status`,
      )
      .json<WorkspacePlaybook>()
  },

  setWorkspacePlaybookDoer: async (
    workspacePlaybookId: string,
    doerId: string | null,
  ): Promise<WorkspacePlaybook> => {
    return api
      .patch({ doer_id: doerId }, `/workspace/playbooks/${workspacePlaybookId}/doer`)
      .json<WorkspacePlaybook>()
  },

  submitFounderBrief: async (input: {
    playbookId: string
    playbookBriefId: string | null | undefined
    briefContent: Json
    founderId?: string | null
  }): Promise<PlaybookBrief> => {
    let playbookBriefId = input.playbookBriefId ?? null

    if (!playbookBriefId) {
      const created = await playbookService.createBrief(input.playbookId, input.briefContent, {
        founderId: input.founderId,
      })
      playbookBriefId = created.id
    }

    const updated = await playbookService.submitBrief(playbookBriefId, input.briefContent, input.founderId)
    return updated
  },

  submitBrief: async (
    userPlaybookId: string,
    briefContent: Json,
    founderId?: string | null,
  ): Promise<PlaybookBrief> => {
    return api
      .put(
        {
          brief: briefContent,
          status: 'in_progress',
        },
        withUserId(`/do/playbooks/${userPlaybookId}`, founderId),
      )
      .json<PlaybookBrief>()
  },

  submitOutput: async (input: {
    /** workspace_playbooks.id (kanban cards use this as the modal "id") */
    workspacePlaybookId?: string | null
    /** playbooks.id (master playbook id) */
    playbookId?: string | null
    output: CreatePlaybookOutputRequest
  }): Promise<{ output: PlaybookOutput }> => {
    const outputPayload = input.output

    let workspacePlaybookId = input.workspacePlaybookId ?? null
    if (!workspacePlaybookId && input.playbookId) {
      const workspace = await playbookService.fetchWorkspaceDetailed()
      workspacePlaybookId = workspace.playbooks.find((wp) => wp.playbook_id === input.playbookId)?.id ?? null
    }
    if (!workspacePlaybookId) throw new Error('workspace_playbook_id missing for output submission')

    // Submit output to the workspace_playbook endpoint (creates playbook_outputs row and marks WP completed).
    let createdOrUpdatedOutput: PlaybookOutput
    try {
      createdOrUpdatedOutput = await api
        .post(outputPayload, `/workspace/playbooks/${workspacePlaybookId}/output`)
        .json<PlaybookOutput>()
    } catch (error: unknown) {
      if (isConflictError(error)) {
        createdOrUpdatedOutput = await api
          .patch(outputPayload, `/workspace/playbooks/${workspacePlaybookId}/output`)
          .json<PlaybookOutput>()
      } else {
        throw error
      }
    }

    return { output: createdOrUpdatedOutput }
  },

  updateOutput: async (input: {
    /** workspace_playbooks.id (kanban cards use this as the modal "id") */
    workspacePlaybookId?: string | null
    /** playbooks.id (master playbook id) */
    playbookId?: string | null
    output: UpdatePlaybookOutputRequest
  }): Promise<{ output: PlaybookOutput }> => {
    const outputPayload = input.output

    let workspacePlaybookId = input.workspacePlaybookId ?? null
    if (!workspacePlaybookId && input.playbookId) {
      const workspace = await playbookService.fetchWorkspaceDetailed()
      workspacePlaybookId = workspace.playbooks.find((wp) => wp.playbook_id === input.playbookId)?.id ?? null
    }
    if (!workspacePlaybookId) throw new Error('workspace_playbook_id missing for output update')

    const updatedOutput = await api
      .patch(outputPayload, `/workspace/playbooks/${workspacePlaybookId}/output`)
      .json<PlaybookOutput>()

    return { output: updatedOutput }
  },

  fetchPlaybookBrief: async (userPlaybookId: string): Promise<PlaybookBrief> => {
    return api.get(`/do/playbooks/${userPlaybookId}`).json<PlaybookBrief>()
  },

  // TODO: Realtime subscriptions not supported by platform API yet.
  // This stub prevents errors from callers expecting subscription interface.
  subscribeToAssignedPlaybooks: (
    doerId: string,
    callback: (payload: unknown) => void,
  ): { unsubscribe: () => void } => {
    void doerId
    void callback
    return {
      unsubscribe: () => {},
    }
  },

  fetchProjectBriefs: async (
    projectId: string,
  ): Promise<{ founder_brief: PlaybookBrief | null; doer_brief: PlaybookBrief | null }> => {
    return api
      .get(`/v1/projects/${projectId}/brief`)
      .json<{ founder_brief: PlaybookBrief | null; doer_brief: PlaybookBrief | null }>()
  },

  submitDoerBrief: async (projectId: string, brief: Json): Promise<PlaybookBrief> => {
    return api.post({ brief }, `/v1/projects/${projectId}/brief`).json<PlaybookBrief>()
  },

  /**
   * Correct an already-submitted doer brief. Answers only — unlike
   * `submitDoerBrief` this fires no ClickUp side effects and moves no statuses,
   * so it is safe to run on a project the Doing Team is already working.
   */
  updateDoerBrief: async (projectId: string, brief: Json): Promise<PlaybookBrief> => {
    return api.put({ brief }, `/v1/projects/${projectId}/brief`).json<PlaybookBrief>()
  },

  removeChannel: (channel: { unsubscribe: () => void }): void => {
    channel.unsubscribe()
  },

  fetchPlaybookFeedback: async (workspacePlaybookId: string): Promise<PlaybookFeedback> => {
    return api.get(`/workspace/playbooks/${workspacePlaybookId}/feedback`).json<PlaybookFeedback>()
  },

  fetchPlaybookOutput: async (workspacePlaybookId: string): Promise<PlaybookOutput> => {
    return api.get(`/workspace/playbooks/${workspacePlaybookId}/output`).json<PlaybookOutput>()
  },

  submitRating: async (
    userPlaybookId: string,
    rating: number,
    feedback: string,
  ): Promise<PlaybookFeedback> => {
    return api
      .post({ rating, feedback }, `/workspace/playbooks/${userPlaybookId}/feedback`)
      .json<PlaybookFeedback>()
  },

  addUserPlaybook: async (playbookId: string): Promise<WorkspacePlaybook> => {
    return api.post({}, `/workspace/playbooks/${playbookId}`).json<WorkspacePlaybook>()
  },

  removeUserPlaybook: async (playbookId: string): Promise<void> => {
    // API returns 204 No Content
    await api.delete(`/workspace/playbooks/${playbookId}`).res()
  },

  /**
   * Restart a completed playbook: archives the active run and creates a fresh
   * `to_be_briefed` one. `playbookId` is the MASTER playbook id or slug (same
   * addressing as `addUserPlaybook`). When `copyFrom` (a prior run's
   * workspace_playbook id) is set, the new run's brief is seeded from that run's
   * answers as a draft. Returns the fresh `workspace_playbooks` row (201);
   * throws a 409 (`code: 'playbook_not_completed'`) if the run isn't completed.
   */
  restartWorkspacePlaybook: async (
    playbookId: string,
    options?: { copyFrom?: string | null },
  ): Promise<WorkspacePlaybook> => {
    const copyFrom = options?.copyFrom ?? null
    return api
      .post(copyFrom ? { copy_from: copyFrom } : {}, `/workspace/playbooks/${playbookId}/restart`)
      .json<WorkspacePlaybook>()
  },

  /**
   * List a playbook's archived (previous) runs as read-only history, newest
   * first. `workspacePlaybookId` is the ACTIVE run's id (what the detail page
   * holds); each returned run's output/feedback is fetched on demand via the
   * existing `/output` and `/feedback` routes by that run's id, and its brief
   * via `/do/playbooks/<playbook_brief_id>`.
   */
  fetchArchivedRuns: async (workspacePlaybookId: string): Promise<ArchivedRun[]> => {
    return api.get(`/workspace/playbooks/${workspacePlaybookId}/archived-runs`).json<ArchivedRun[]>()
  },

  requestRevision: async (workspacePlaybookId: string, notes: string) => {
    return api.post({ notes }, `/workspace/playbooks/${workspacePlaybookId}/revisions`).json()
  },

  fetchRevisions: async (workspacePlaybookId: string): Promise<PlaybookRevision[]> => {
    return api.get(`/workspace/playbooks/${workspacePlaybookId}/revisions`).json<PlaybookRevision[]>()
  },
}
